import './styles/EventSettings.scss';

import { useMutation, useQuery } from '@apollo/client';
import { Button, Grid, Paper, TextField, Typography } from '@mui/material';
import { EventSetting } from '@thro-types/events';

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router';

import MUTATION from 'gql/mutations/EventService/updateEventSettings.gql';
import QUERY from 'gql/queries/EventService/getEventSetting.gql';

import { GQLGuard } from 'app/components';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';
import usePageHeading from 'utils/hooks/usePageHeading';

enum Color {
  RED = 'red',
  YELLOW = 'yellow',
  GREEN = 'green'
}

export const EventSettings = () => {
  const { t } = useTranslation();
  const [green, setGreen] = useState(0);
  const [yellow, setYellow] = useState(0);
  const [red, setRed] = useState(0);
  const navigate = useNavigate();

  const [retentionTime, setRetentionTime] = useState(0);
  const dispatch = useDispatch();

  const query = useQuery(QUERY, { context: { clientName: 'federation' } });

  const [updateEventSettings] = useMutation(MUTATION);

  usePageHeading({
    heading: t(translations.pages.eventSettings.title()),
    mobilePadding: false
  });

  const handleBlur = (color: Color, value: number) => {
    switch (color) {
      case Color.GREEN:
        if (yellow < value) setYellow(value);
        if (red < value) setRed(value);
        break;
      case Color.YELLOW:
        if (green > value) setGreen(value);
        if (red < value) setRed(value);
        break;
      case Color.RED:
        if (green > value) setGreen(value);
        if (yellow > value) setYellow(value);

        break;
    }
  };

  const handleChange = (color: Color, value: number) => {
    const checkedValue = value > 100 ? 100 : value;
    switch (color) {
      case Color.GREEN:
        setGreen(checkedValue);
        break;
      case Color.YELLOW:
        setYellow(checkedValue);
        break;
      case Color.RED:
        setRed(checkedValue);

        break;
    }
  };

  useEffect(() => {
    if (query.data !== undefined && query.data !== null) {
      initializeSettingsData(query.data.getEventSetting);
    }
  }, [query.data]);

  const initializeSettingsData = (setting: EventSetting) => {
    setGreen(setting.occupancyGreen);
    setYellow(setting.occupancyYellow);
    setRed(setting.occupancyRed);
    setRetentionTime(setting.retentionTimeInDays);
  };

  const handleUpdateEventSettings = async id => {
    try {
      await updateEventSettings({
        variables: {
          id: id,
          data: {
            retentionTimeInDays: retentionTime,
            occupancyRed: red,
            occupancyYellow: yellow,
            occupancyGreen: green
          }
        },
        context: { clientName: 'federation' },
        refetchQueries: [QUERY]
      });
      dispatch(AlertActions.success(t(translations.pages.eventSettings.success.update())));
    } catch (err) {
      dispatch(AlertActions.error(t(translations.pages.eventSettings.error.update())));
    }
  };

  return (
    <GQLGuard
      query={query}
      onError={error => {
        dispatch(AlertActions.error(`${t(translations.pages.eventSettings.error.loading())}`));
        navigate('/', { replace: true });
        return null;
      }}
    >
      {settings => (
        <Grid component={Paper} className="EventSettings">
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography component="h2">
                {t(translations.pages.eventSettings.coloredOccupancyTitle())}
              </Typography>
              <Typography component="h3">
                {t(translations.pages.eventSettings.coloredOccupancyDescription())}
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <TextField
                type="number"
                value={green}
                onChange={e => handleChange(Color.GREEN, parseInt(e.target.value, 10))}
                onBlur={e => handleBlur(Color.GREEN, parseInt(e.target.value, 10))}
                InputProps={{ inputProps: { min: 0, max: 100 } }}
                label={t(translations.pages.eventSettings.colors[Color.GREEN]())}
                fullWidth
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                value={yellow}
                label={t(translations.pages.eventSettings.colors[Color.YELLOW]())}
                fullWidth
                InputProps={{ inputProps: { min: 0, max: 100 } }}
                type="number"
                onChange={e => handleChange(Color.YELLOW, parseInt(e.target.value, 10))}
                onBlur={e => handleBlur(Color.YELLOW, parseInt(e.target.value, 10))}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                value={red}
                label={t(translations.pages.eventSettings.colors[Color.RED]())}
                fullWidth
                InputProps={{ inputProps: { min: 0, max: 100 } }}
                type="number"
                onChange={e => handleChange(Color.RED, parseInt(e.target.value, 10))}
                onBlur={e => handleBlur(Color.RED, parseInt(e.target.value, 10))}
              />
            </Grid>
          </Grid>
          <Grid container style={{ paddingTop: '1em' }} spacing={2}>
            <Grid item xs={12}>
              <Typography component="h2">{t(translations.pages.eventSettings.period())}</Typography>
            </Grid>
            <Grid item xs={4}>
              <TextField
                value={retentionTime}
                fullWidth
                InputProps={{ inputProps: { min: 0 } }}
                type="number"
                onChange={e => {
                  const value = parseInt(e.target.value, 10) < 0 ? 0 : parseInt(e.target.value, 10);
                  setRetentionTime(value);
                }}
              />
            </Grid>
          </Grid>
          <Grid container style={{ paddingTop: '1em' }} spacing={2} justifyContent={'flex-end'}>
            <Grid item>
              <Button
                variant="contained"
                style={{ color: 'white' }}
                onClick={() => {
                  handleUpdateEventSettings(settings.getEventSetting.id);
                }}
              >
                {t(translations.common.save())}
              </Button>
            </Grid>
          </Grid>
        </Grid>
      )}
    </GQLGuard>
  );
};
